import { useState } from 'react'
import Link from 'next/link'
import ImageLoader from '../ImageLoader'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import {useTranslation} from "react-i18next";
import { useRouter } from 'next/router';

// Same frame for every card photo (Bootstrap .ratio with a 3:2 aspect)
const CARD_PHOTO_RATIO = { '--bs-aspect-ratio': '66.6667%' }

const CardImageSlider = ({ label, horizontal, images, href, badges, ribbon, wishlistButton, light }) => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();
  const isRTL = locale === 'ar';

  // The card shows its first photo until the visitor interacts with it; only
  // then is the Swiper mounted. A page of cards would otherwise start a slider
  // per card on load (layout reads on every one) and put every photo in the HTML.
  // Swiper's code is fetched on that first interaction; the static photo stays
  // on screen until it has arrived (no blank card while loading)
  const [CardSwiper, setCardSwiper] = useState(null)
  const activate = () => {
    if (CardSwiper) return
    import('./CardSwiper').then((mod) => setCardSwiper(() => mod.default))
  }
  const canSlide = images.length > 1

  const renderImage = (image) => horizontal ? <ImageLoader
    src={image[0]}
    alt={label || ''}
    layout='fill'
    objectFit='cover'
    sizes='(max-width: 767px) 100vw, 40vw'
    light={light ? 1 : 0}
  /> : <div className='ratio w-100' style={CARD_PHOTO_RATIO}>
    {/* Fixed 3:2 frame (the usual 1254x836 photo), photo cropped to fill it:
        portrait or odd-sized photos no longer make some cards taller */}
    <ImageLoader
      src={image[0]}
      alt={label || image[3]}
      layout='fill'
      sizes='(max-width: 575px) 100vw, (max-width: 991px) 50vw, 33vw'
      objectFit='cover'
      light={light ? 1 : 0}
    />
  </div>

  const overlays = <>
      {href ? <Link href={href} className='img-overlay' aria-label={label}></Link> : <span className='img-overlay'></span>}
      {ribbon && <div
          className={`position-absolute  text-white px-3 py-1 zindex-5`}
          style={{
            transform: isRTL ? 'rotate(-45deg)' : 'rotate(45deg)',
            width: '200px',
            ...(isRTL ? { left: '-60px' } : { right: '-60px' }),
            top: '24px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: ribbon[0],
          }}
      >
        <span style={{color: ribbon?.[2] ?? '#fff'}}>
          {t(ribbon[1])}
        </span>
      </div>}
      {badges && <div className={`position-absolute top-0 pt-3 ${isRTL ? 'end-0 pe-3' : 'start-0 ps-3'}`}>
        {badges.map((badge, indx) => {
          return <span key={indx} className={`d-table badge bg-${badge[0]} mb-1`}>{t(badge[1])}</span>
        })}
      </div>}
      {wishlistButton && <div className={`${wishlistButton.active ? 'position-absolute zindex-5' : 'content-overlay'} end-0 top-0 pt-3 pe-3`}>
        {wishlistButton.tooltip ? <OverlayTrigger
          placement='left'
          overlay={<Tooltip>{wishlistButton.tooltip}</Tooltip>}
        >
          <button
            {...wishlistButton.props}
            type='button'
            className='btn btn-icon btn-light btn-xs text-primary rounded-circle'
          >
            <i className={wishlistButton.active ? 'fi-heart-filled' : 'fi-heart'}></i>
          </button>
        </OverlayTrigger> : <button
          {...wishlistButton.props}
          type='button'
          className='btn btn-icon btn-light btn-xs text-primary rounded-circle'
        >
          <i className={wishlistButton.active ? 'fi-heart-filled' : 'fi-heart'}></i>
        </button>}
      </div>}
  </>

  if (!CardSwiper || !canSlide) {
    return (
      <div
        className='card-img-top card-img-hover position-relative d-flex overflow-hidden'
        onPointerEnter={canSlide ? activate : undefined}
        onTouchStart={canSlide ? activate : undefined}
        onFocus={canSlide ? activate : undefined}
      >
        {images[0] && renderImage(images[0])}
        {overlays}
      </div>
    )
  }

  return <CardSwiper images={images} renderImage={renderImage} overlays={overlays} isRTL={isRTL} />
}

export default CardImageSlider
